import { createSelector } from '@reduxjs/toolkit';
import { getLoadingCUTranslationFromDevice } from '../../CUTranslation/selectors';
import { getMyWorkspaceSelector } from '../../stoerkID/selectors/StoerkId.selectors';
import ControlUnitAPI from '../../controlUnit/api/ControlUnitAPI';
import { getDevicesDataLoadingById } from '../../commander/selectors';
import { useGroupById } from '../../groups/hooks/useGroupById';
import { useAppSelector } from '../../store.model';

/**
 * Get composable device loading state
 *
 * This is a hook (it uses useGroupById and useAppSelector), so it must be called
 * at the top level of a component, never inside another selector.
 */
export const useComposableDeviceIsLoadingByGroupId = (props: {
  groupId?: string;
}): boolean => {
  const { data: group, isLoading: loadingGroup } = useGroupById(props.groupId);
  const deviceIds = group?.devices ?? [];

  const isLoadingCUs = useAppSelector((state) => {
    const loadingDeviceId = getDevicesDataLoadingById(state);
    return deviceIds.some((deviceId) => {
      const loadingControlUnits =
        !!ControlUnitAPI.endpoints.getControlUnitById.select(deviceId)(state)
          .isLoading;
      const loadingControlUnitsEvents =
        ControlUnitAPI.endpoints.getCurrentEventsByCommanderID.select(deviceId)(
          state
        ).isLoading;
      const loadingControlUnitsSensorData =
        ControlUnitAPI.endpoints.getCurrentSensorDataByCommanderID.select(
          deviceId
        )(state).isLoading;

      const loadingControlUnitsTranslations =
        !!getLoadingCUTranslationFromDevice(state, { deviceId });
      return (
        loadingDeviceId ||
        loadingControlUnits ||
        loadingControlUnitsEvents ||
        loadingControlUnitsSensorData ||
        loadingControlUnitsTranslations
      );
    });
  });

  return loadingGroup || isLoadingCUs;
};

export const getMachinesTypeSelector = createSelector(
  [getMyWorkspaceSelector],
  (myWorkspace) => {
    return myWorkspace?.machineTypes || [];
  }
);

export const getMachineTypeSelector = createSelector(
  [
    getMachinesTypeSelector,
    (state: any, props: { machineTypeId?: string }) => props.machineTypeId,
  ],
  (machineTypes, machineTypeId: string | undefined) => {
    return machineTypes.find((machineType) => machineType.id === machineTypeId);
  }
);

export const getMachineTypeById = createSelector(
  [
    getMachinesTypeSelector,
    (state: any, machineTypeId?: string) => machineTypeId,
  ],
  (machineTypes, machineTypeId: string | undefined) => {
    return machineTypes.find((machineType) => machineType.id === machineTypeId);
  }
);
